import { ReactNode, useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"

import { AdminTokenService } from "services/token/AdminTokenService"

import { Translate } from "../../helpers/token.helpers"
import { classifyTokenFailure, TokenGateState } from "../../helpers/useTokenGate"

import { TokenUnavailable } from "./TokenUnavailable"

/**
 * Resolves the per-tenant $TOKEN gate ONCE, at the shell, before any tab renders.
 *
 * 🔴 WHY THIS EXISTS RATHER THAN LETTING EACH TAB DISCOVER THE GATE ITSELF.
 *
 * The first version let every tab probe independently. On a tenant where $TOKEN
 * is not enabled that produced a fully working-looking page — tab strip, tenant
 * selector, clickable Presale/Pools/Analytics/Manual ops — with the refusal
 * hidden one level down inside whichever tab happened to be open. An operator
 * could click through four tabs, fire four failing requests, and reasonably
 * conclude the feature was broken rather than switched off.
 *
 * A closed gate is a property of the TENANT, not of a tab. So it is resolved
 * where the tenant is chosen, and when it is closed there are no tabs to click:
 * one screen, one explanation.
 *
 * `GetConfig` is the probe because it is the cheapest authenticated call that
 * passes through the same `isTokenEnabled` gate as everything else — there is no
 * separate "is this on?" endpoint to drift away from the real check.
 *
 * ⚠️ This is a UX gate, NOT a security boundary. Every endpoint re-checks
 * `isTokenEnabled` server-side on every call; hiding the tabs stops confusion,
 * not a determined caller. Never move an authorization decision here.
 */
export const TokenGateBoundary = ({ tenant, children }: { tenant: string; children: ReactNode }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const [state, setState] = useState<TokenGateState | "loading">("loading")

  const probe = useCallback(async () => {
    if (!tenant) return
    setState("loading")
    const res = await AdminTokenService.getConfig(tenant)

    if (res.ok) {
      setState("ok")
      return
    }

    const classified = classifyTokenFailure(res)

    // CONFIG_MISSING means the tenant IS enabled but has no presale row yet —
    // a real pre-launch state that the Presale tab can explain far better than a
    // blanket "unavailable" screen. Let it through rather than blocking the page
    // on a condition an admin may be about to fix.
    setState(classified === "ok" && res.code !== "CONFIG_MISSING" ? "unavailable" : classified)
  }, [tenant])

  useEffect(() => {
    probe()
  }, [probe])

  if (state === "loading") return <LoadingIndicator text={t("token.gateChecking")} />
  if (state !== "ok") return <TokenUnavailable variant={state} onRetry={probe} />

  return <>{children}</>
}
